/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '../../hooks/common/use-application-state'
import { renameNoteTreeEntry, updateNoteTreeEntry } from '../../redux/note-tree/methods'
import { useEffect, useRef } from 'react'

const SYNC_DELAY_MS = 1000

/**
 * Updates the currently edited note in the tree as soon as its title, path, badge or primary alias changes.
 * The backend only picks up title changes when the next revision is saved, so without this the author
 * would have to wait for the next save and tree refresh to see the note with its new title.
 */
export const useSyncCurrentNoteIntoTree = (): void => {
  const isSynced = useApplicationState((state) => state.realtimeStatus.isSynced)
  const treeLoaded = useApplicationState((state) => state.noteTree.loaded)
  const primaryAlias = useApplicationState((state) => state.noteDetails?.primaryAlias)
  const aliases = useApplicationState((state) => state.noteDetails?.aliases)
  const title = useApplicationState((state) => state.noteDetails?.title)
  const path = useApplicationState((state) => state.noteDetails?.path)
  const badge = useApplicationState((state) => state.noteDetails?.badge)
  const treeEntry = useApplicationState((state) =>
    primaryAlias === undefined ? undefined : state.noteTree.entries[primaryAlias]
  )
  const previousPrimaryAlias = useRef<string | undefined>(undefined)

  // Move the tree entry if the primary alias of the same note changed
  useEffect(() => {
    const previousAlias = previousPrimaryAlias.current
    previousPrimaryAlias.current = primaryAlias
    if (!primaryAlias || !previousAlias || previousAlias === primaryAlias || !aliases?.includes(previousAlias)) {
      return
    }
    renameNoteTreeEntry({ from: previousAlias, to: primaryAlias })
  }, [primaryAlias, aliases])

  useEffect(() => {
    // Before the content is synced, the title might not be extracted yet
    if (!isSynced || !treeLoaded || !primaryAlias || title === undefined || path === undefined || badge === undefined) {
      return
    }
    if (treeEntry !== undefined && treeEntry.title === title && treeEntry.path === path && treeEntry.badge === badge) {
      return
    }
    const timeoutId = window.setTimeout(() => updateNoteTreeEntry({ primaryAlias, title, path, badge }), SYNC_DELAY_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isSynced, treeLoaded, primaryAlias, title, path, badge, treeEntry])
}
